import {createSelector} from 'reselect';

import {HTTP_CODES} from '@stubhub/general-utils';
import {Controller} from '@stubhub/react-store-provider';
import SessionHelper from '@stubhub/session-helper';

import SearchInventoryV2 from '../../../../models/listing_search_inventory_v2';
import {storeIdSelector} from '../../../../store/gsConfig/selectors';
import FEATURE_FLAGS from '../../../../utils/featureFlags';
import {print_at_home, physical_ticket, mobile, mobile_tranfer} from '../../utils/ticketTypeConstants';

import {enhanceListings} from './reducers';

const QUERY_PAGE_LIMIT = 20;
const eventDataSelector = createSelector(
  (state) => state.event || {},
  (event) => {
    return {
      venueConfig: event.venueConfig,
      isGA: event.isGA,
      showTicketRecoInd: event.showTicketRecoInd,
      seatTraits: event.seatTraits,
    };
  }
);

const controller = new Controller({
  namespace: 'listings',
  mapStateToProps(state) {
    const localState = this.getLocalState(state) || {};

    return {
      listings: localState.data || [],
      offset: localState.offset || 0,
      hasMore: localState.hasMore,
      loading: localState.loading,
      page: localState.page,
      sectionStats: localState.sectionStats,
      zoneStats: localState.zoneStats,
      additionalEventLevelPriceInfo: localState.additionalEventLevelPriceInfo,
      additionalSectionLevelPriceInfo: localState.additionalSectionLevelPriceInfo,
      eventPricingSummary: localState.eventPricingSummary || {},
      pricingSummary: localState.pricingSummary || {},
      shstore: storeIdSelector(state),
      ...eventDataSelector(state),
    };
  },
  actionCreators: {
    fetchInventoryListings,
    clearHoveredSection,
    setHoveredSection,
    setPage,
  },
  reducers: [
    'EVENT:LISTING_SELECTED',
    {action: 'EVENT:LISTING_LOADED', handler: enhanceListings},
    'EVENT:LISTING_LOADING',
    'EVENT:LISTING_ERROR',
    'EVENT:LISTING_NEW_PAGE',
  ],
});

// Controller.addResetReducer(['EVENT:LISTING_RESET']);

function setPage(page) {
  return (dispatch) => {
    dispatch({type: 'EVENT:LISTING_NEW_PAGE', page});
  };
}

function clearHoveredSection() {
  return (dispatch) => {
    dispatch({type: 'EVENT:TICKETLISTPANEL_HOVER', sectionId: 0});
  };
}

function setHoveredSection(sectionId) {
  return (dispatch) => {
    dispatch({type: 'EVENT:TICKETLISTPANEL_HOVER', sectionId});
  };
}

const featureListToArray = (features) => {
  if (typeof features === 'number') {
    return [Number(features)];
  } else if (Array.isArray(features)) {
    return features.map((entry) => Number(entry));
  }

  return [];
};

function getFilterCount(opts) {
  let count = 0;

  opts.quantity && count++;
  opts.priceWithFees && count++;
  (opts.sliderMin || opts.sliderMax) && count++;
  count += featureListToArray(opts.excludeCategory).length;
  count += featureListToArray(opts.listingCategory).length;

  const deliveryTypes = [print_at_home, physical_ticket, mobile, mobile_tranfer]; // These numbers map to the TicketType Buckets
  count += featureListToArray(opts.deliveryType).filter((type) => {
    return deliveryTypes.includes(type);
  }).length;

  return count;
}

function fetchInventoryListings(eventId, query = {}, reset) {
  return async (dispatch, getState, {cookies}) => {
    try {
      const {data: currentListings = [], offset: currentOffset, currency: currentCurrency} = getState().listings || {};
      if (reset) {
        query.page = 1;
      }
      const filterCount = getFilterCount(query);
      dispatch({type: 'EVENT:LISTING_LOADING', listingLoading: true, error: undefined});

      const {
        additionalEventLevelPriceInfo = [],
        additionalSectionLevelPriceInfo = [],
        eventStats = {},
        currency,
        sectionStats,
        zoneStats,
        hasMore,
        hasTickets,
        totalListings,
        totalTickets = -1,
        listings,
        offset,
        eventPricingSummary = {},
        pricingSummary = {},
        error,
      } = await getListings(eventId, {...query, offset: currentOffset}, {reset, state: getState(), cookies});
      if (!error) {
        dispatch({
          type: 'EVENT:LISTING_LOADED',
          eventId,
          currency: currency || currentCurrency,
          offset,
          additionalEventLevelPriceInfo,
          additionalSectionLevelPriceInfo,
          hasMore,
          hasTickets,
          totalTickets,
          totalListings,
          listingLoading: false,
          data: reset ? listings : currentListings.concat(listings),
          page: query.page,
          eventStats,
          sectionStats,
          zoneStats,
          quantity: query.quantity,
          eventPricingSummary,
          pricingSummary,
          availableQuantities: getAvailableQuantities(eventStats),
          filterCount,
          error: undefined,
        });
      } else {
        dispatch({
          type: 'EVENT:LISTING_ERROR',
          error,
        });
      }
    } catch (e) {
      dispatch({
        type: 'EVENT:LISTING_ERROR',
        error: e,
      });
    }
  };
}

/**
 * If the query for event information fails with 401, we need to logout the user and ask again.
 */
const getSearchInventoryV2 = async (lang, eventId, query, caEvent, features, cookies) => {
  const getRequestParams = {
    ...getListingsOpts(eventId, {...query}, caEvent, features),
    cookies,
  };
  try {
    return await SearchInventoryV2({lang}).get(getRequestParams);
  } catch (error) {
    if (error.status === HTTP_CODES.UNAUTHORIZED) {
      const domain = __CLIENT__ && window.location.host.split('www').pop().split(':')[0];
      await SessionHelper.logout({domain});

      return await SearchInventoryV2({lang}).get(getRequestParams);
    }
    throw error;
  }
};

async function getListings(eventId, query, {reset, state, cookies}) {
  let {offset = 0} = query;

  const {venue = {}} = state.event || {};
  const {defaultWebTLD = '', features = {}} = state.gsConfig || {};
  const caEvent =
    venue.country && venue.country.toLowerCase() === 'ca' && defaultWebTLD && defaultWebTLD.toLowerCase() === 'ca';

  const body = await getSearchInventoryV2(state.lang, eventId, query, caEvent, features, cookies);

  const {
    totalTickets,
    totalListings = 0,
    listing: listings = [],
    eventLevelStats = {},
    sectionStats = [],
    zoneStats = [],
    eventPricingSummary = {},
    pricingSummary = {},
    additionalEventLevelPriceInfo = [],
    additionalSectionLevelPriceInfo = [],
    currency,
  } = body;

  const {quantitySummary = {}} = eventLevelStats;
  const hasTickets = !!Object.keys(quantitySummary).length;
  const eventStats = {...eventLevelStats};
  offset = reset ? 0 : offset;
  offset += listings.length;
  const hasMore = offset < totalListings && listings.length === QUERY_PAGE_LIMIT;

  return {
    additionalEventLevelPriceInfo,
    additionalSectionLevelPriceInfo,
    eventStats,
    hasMore,
    offset,
    sectionStats,
    zoneStats,
    listings,
    hasTickets,
    totalTickets,
    eventPricingSummary,
    pricingSummary,
    totalListings,
    currency,
  };
}

function joinList(list) {
  if (list && Array.isArray(list)) {
    return list.length > 1 && list.join();
  }

  return list;
}

/**
 * Function to run only once on initial load of the page
 * to load the bundled price for events happening in CA and
 * accessed in .ca domain
 */
const checkCAEventOnce = (function () {
  let executed = false;

  return function (caEvent) {
    if (!executed) {
      executed = true;

      return caEvent;
    }

    return false;
  };
})();

function getListingsOpts(eventId, options, caEvent, features) {
  let {
    offset = 0,
    sort = 'price asc, value desc',
    listingCategory: listingAttributeCategoryList,
    sectionIdList,
    zoneIdList,
    deliveryType: deliveryTypeList,
    valuePercentage = true,
    quantity,
  } = options;
  const {
    page = 1,
    zoneStats = true,
    allSectionZoneStats = true,
    eventLevelStats = true,
    quantitySummary = true,
    limit = QUERY_PAGE_LIMIT,
    shstore = 1,
    edgeControlEnabled = true,
    priceWithFees,
    additionalPricingInfo = true,
    listingAttributeCategorySummary = true,
    listingRows,
    excludeCategory: excludeListingAttributeCategoryList,
    sliderMin = [],
    sliderMax = [],
  } = options;

  sort = joinList(sort);
  sectionIdList = joinList(sectionIdList);
  zoneIdList = joinList(zoneIdList);
  listingAttributeCategoryList = joinList(listingAttributeCategoryList);
  deliveryTypeList = joinList(deliveryTypeList);

  const [pricemin] = sliderMin;
  const [pricemax] = sliderMax;

  let priceType = 'nonBundledPrice';
  if (checkCAEventOnce(caEvent) || priceWithFees) {
    priceType = 'bundledPrice';
  }

  offset = page > 1 ? offset : 0;
  if (quantity === 0) {
    quantity = null;
  }
  if (quantity === 5) {
    quantity = '>5';
  }

  valuePercentage = features[FEATURE_FLAGS.event.showValuePercentage] && valuePercentage;

  return {
    start: offset,
    sectionStats: true,
    zoneStats,
    eventId,
    allSectionZoneStats,
    eventLevelStats,
    quantitySummary,
    quantity,
    rows: limit,
    shstore,
    sort,
    additionalPricingInfo,
    pricingSummary: true,
    eventPricingSummary: true,
    edgeControlEnabled,
    priceType,
    valuePercentage,
    listingAttributeCategorySummary,
    listingAttributeCategoryList,
    excludeListingAttributeCategoryList,
    deliveryTypeList,
    sectionIdList,
    zoneIdList,
    pricemin,
    pricemax,
    listingRows,
  };
}

function getAvailableQuantities({quantitySummary = []}) {
  return Object.keys(quantitySummary)
    .map((e) => quantitySummary[e])
    .reduce((result, stat) => {
      if (stat.totalListings) {
        result.push(stat.quantity);
      }

      return result;
    }, [])
    .sort((a, b) => a - b);
}

export default controller;
